import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiEye, FiFileText, FiPrinter, FiXCircle
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, SortableTh, SortContext
} from '../../components/SharedStyles';
import { getItemsCached, getEmployeesCached, getJobsCached, getLocationsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import {
  createTransaction, updateTransaction, deleteTransaction,
  updateTransactionStatus
} from '../../utils/transactionHelper';
import { deleteStockFlow, getItemStockCached, invalidateItemStock } from '../../utils/stockFlowHelper';
import LineUnitPicker from '../../components/LineUnitPicker';
import { seedLineUnitFromItem, applyUnitChangeToLine } from '../../utils/unitConversion';
import { deleteSubTransactions } from '../../utils/subTransactionHelper';
import PageLayout from '../../components/PageLayout';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import { useLocation } from 'react-router-dom';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import ItemSelect from '../../components/ItemSelect';
import { isFutureDate } from '../../utils/validationHelpers';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';

const TRAN_TYPE_CODE = 'AS';

// Footer summary spec — aggregated server-side in paged mode,
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Posted']
};

const EMPTY_LINE = { itemId: '', description: '', quantity: 1, unitOfMeasure: '', unitCode: '', unitCost: 0, lineTotal: 0, trackingType: 'None', batchNumber: '', expiryDate: '', serialNumbers: [] };

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Posted: colors.success.main,
  Cancelled: colors.error.main,
};

const AddStock = () => {
  const { activeCompany, currentUser, activeFinancialYear, activeBranch } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('transactions', 'addStock');
  const location = useLocation();
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: records,
    loading,
    refresh: loadRecords,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docNumber: '', docDate: new Date().toISOString().split('T')[0],
    adjustmentType: 'Found', reason: '', notes: '', status: 'Draft',
    employeeId: '', employeeName: '', jobId: '', jobName: '',
    locationId: '', locationName: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stockBalances, setStockBalances] = useState({});
  const [printConfig, setPrintConfig] = useState(null);

  // The record list itself loads inside useDocListPagination above
  // (loadRecords = its refresh). The old full-history getTransactions load lived here.

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Tier 1: items come from the device-local master cache (0 Firestore reads/open).
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) {
      console.error('Error loading items:', error);
    }
  }, [activeCompany]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  // Firebase cost: stock is fetched ONE ITEM AT A TIME, on demand. The old
  // mount-time load scanned the company's ENTIRE stockFlow history just to draw
  // badges the user may never look at. Now the dropdown shows a "stock?" chip
  // and this runs only when the user taps it.
  const requestItemStock = useCallback(async (itemId) => {
    if (!activeCompany?.id || !itemId) return;
    try {
      const qty = await getItemStockCached(activeCompany.id, itemId, {
        branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined
      });
      setStockBalances(prev => ({ ...prev, [itemId]: qty }));
    } catch (e) { console.error('Error loading item stock:', e); }
  }, [activeCompany, activeBranch]);

  // NOTE: the record list itself loads inside useDocListPagination — not here.
  useEffect(() => { loadItems(); loadEmployees(); loadJobs(); loadLocations(); loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [loadItems, loadEmployees, loadJobs, loadLocations]);

  const filteredRecords = useMemo(() => {
    let filtered = records;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.docNumber?.toLowerCase().includes(q) || r.reason?.toLowerCase().includes(q) || r.adjustmentType?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [records, statusFilter, searchQuery, sort]);

  const calculateLine = (line) => ({
    ...line,
    unitPrice: line.unitCost || 0,
    lineTotal: Math.round(line.quantity * line.unitCost * 100) / 100
  });

  const totals = useMemo(() => {
    const lines = lineItems.map(calculateLine);
    return { totalValue: Math.round(lines.reduce((s, l) => s + l.lineTotal, 0) * 100) / 100 };
  }, [lineItems]);

  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].description = item.itemName || '';
          updated[index].trackingType = item.trackingType || 'None';
          updated[index].batchNumber = '';
          updated[index].expiryDate = '';
          updated[index].serialNumbers = [];
          const seeded = seedLineUnitFromItem(item, { priceField: 'purchasePrice', defaultUnitField: 'defaultPurchaseUnit' });
          updated[index] = { ...updated[index], unitCode: seeded.unitCode, unitOfMeasure: seeded.unitOfMeasure, unitCost: seeded.unitCost };
        }
      }
      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'purchasePrice' });
          updated[index] = { ...updated[index], unitCode: patch.unitCode, unitOfMeasure: patch.unitOfMeasure, unitCost: patch.unitCost };
        }
      }
      updated[index] = calculateLine(updated[index]);
      return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || (Number(line.quantity) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => { if (lineItems.length <= 1) return; setLineItems(prev => prev.filter((_, i) => i !== index)); };

  const resetForm = () => {
    setFormData({ docNumber: '', docDate: new Date().toISOString().split('T')[0], adjustmentType: 'Found', reason: '', notes: '', status: 'Draft',
      employeeId: '', employeeName: '', jobId: '', jobName: '',
      locationId: '', locationName: '' });
    setLineItems([{ ...EMPTY_LINE }]);
    setEditingRecord(null);
  };

  const handleCreate = () => { resetForm(); setViewMode('form'); };
  const handleEdit = (rec) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingRecord(rec);
    setFormData({ docNumber: rec.docNumber || '', docDate: rec.docDate || new Date().toISOString().split('T')[0], adjustmentType: rec.adjustmentType || 'Found', reason: rec.reason || '', notes: rec.notes || '', status: rec.status || 'Draft',
      employeeId: rec.employeeId || '', employeeName: rec.employeeName || '',
      jobId: rec.jobId || '', jobName: rec.jobName || '',
      locationId: rec.locationId || '', locationName: rec.locationName || '' });
    setLineItems(rec.lines?.length > 0 ? rec.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }]);
    setViewMode('form');
  };
  const handleView = (rec) => { setViewingRecord(rec); setViewMode('view'); };

  // Handle navigation from reports. With pagination the target may not be in
  // the loaded page — ensureDoc fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(rec => {
      if (rec) {
        setViewingRecord(rec);
        setViewMode('view');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!formData.reason.trim()) { alert('Please enter a reason'); return; }
    const validLines = lineItems.filter(l => l.itemId && l.quantity > 0);
    if (validLines.length === 0) { alert('Please add at least one item with quantity'); return; }

    for (const line of validLines) {
      if (line.trackingType === 'Batch' && !line.batchNumber?.trim()) {
        alert(`Batch number is required for "${line.description}"`); return;
      }
      if (line.trackingType === 'Serial') {
        const serials = line.serialNumbers || [];
        if (serials.length !== (line.quantity || 0)) {
          alert(`"${line.description}" requires exactly ${line.quantity} serial number(s). You entered ${serials.length}.`); return;
        }
        if (new Set(serials).size !== serials.length) {
          alert(`Duplicate serial numbers found for "${line.description}"`); return;
        }
      }
    }

    // Financial year validation
    if (activeCompany?.currentFinancialYear) {
      const docDate = new Date(formData.docDate);
      const fyStart = new Date(activeCompany.currentFinancialYear.startDate);
      const fyEnd = new Date(activeCompany.currentFinancialYear.endDate);
      if (docDate < fyStart || docDate > fyEnd) {
        alert(`Document date must be within the current financial year (${fyStart.toLocaleDateString()} - ${fyEnd.toLocaleDateString()})`);
        return;
      }
    }

    if (!activeFinancialYear?.id) { alert('No active financial year'); return; }

    // Future date warning
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => {
        const item = items.find(it => it.id === l.itemId);
        return { ...calculateLine(l), lineNumber: i + 1, itemCode: item?.itemCode || '', itemName: item?.itemName || l.description || '' };
      });
      const status = saveStatus || 'Draft';
      const data = {
        tranTypeCode: TRAN_TYPE_CODE,
        financialYearId: activeFinancialYear.id,
        docDate: formData.docDate, adjustmentType: formData.adjustmentType,
        reason: formData.reason, notes: formData.notes, status,
        employeeId: formData.employeeId, employeeName: formData.employeeName,
        jobId: formData.jobId, jobName: formData.jobName,
        locationId: formData.locationId, locationName: formData.locationName,
        totalValue: totals.totalValue, lines: calculatedLines,
        branchId: activeBranch?.id || null, branchName: activeBranch?.branchName || null
      };

      let savedDocId = editingRecord?.id;
      let savedDocNumber = editingRecord?.docNumber;
      let newTranData = null;

      if (editingRecord) {
        await updateTransaction(activeCompany.id, editingRecord.id, data, currentUser.uid);
        savedDocNumber = editingRecord.docNumber;
      } else {
        newTranData = await createTransaction(activeCompany.id, data, currentUser.uid);
        savedDocId = newTranData.id;
        savedDocNumber = newTranData.docNumber;
      }

      // Stock flow is already generated by createTransaction/updateTransaction above
      // No need to call generateStockFlow() again here

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedRecord = editingRecord
        ? { ...editingRecord, ...data, id: savedDocId, status }
        : { ...newTranData, status };
      applyLocal(prev => editingRecord
        ? prev.map(r => r.id === savedDocId ? savedRecord : r)
        : [savedRecord, ...prev]);

      // Stock moved — drop the per-item cached quantities so the next "stock?"
      // lookup (here or on any other form this session) reads fresh numbers.
      invalidateItemStock(activeCompany.id);

      setViewMode('list');
      resetForm();

      // Background refresh — reconciles with server data without blocking UI.
      loadRecords().catch(err => console.error('Background refresh failed:', err));
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rec) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete ${rec.docNumber}?`)) return;
    try {
      await deleteTransaction(activeCompany.id, rec.id);
      await loadRecords();
    } catch (error) { console.error('Error deleting:', error); alert('Error deleting record'); }
  };

  const handleCancel = async (rec) => {
    if (!canEdit) { alert('You do not have permission to cancel.'); return; }
    if (rec.status === 'Cancelled') { alert('Already cancelled'); return; }
    if (rec.status === 'Posted') {
      if (!window.confirm('This will reverse the stock and accounting entries. Continue?')) return;
    }
    if (!window.confirm(`Cancel ${rec.docNumber}?`)) return;
    try {
      if (rec.status === 'Posted') {
        await deleteStockFlow(activeCompany.id, rec.id);
        await deleteSubTransactions(activeCompany.id, rec.id);
      }
      await updateTransactionStatus(activeCompany.id, rec.id, 'Cancelled', currentUser.uid);
      await loadRecords();
    } catch (error) { console.error('Error cancelling:', error); alert('Error cancelling record'); }
  };

  const handlePrint = (rec) => {
    const data = rec || viewingRecord;
    if (!data) return;
    printDocument('addStock', data, activeCompany, printConfig, activeBranch);
  };

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox><FiSearch />
          <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Posted">Posted</option>
            <option value="Cancelled">Cancelled</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Add Stock</TranAddButton>}
        </FilterGroup>
      </ActionBar>
      {loading ? <LoadingMessage>Loading...</LoadingMessage> : filteredRecords.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Add Stock Records</h3>
          <p>{records.length === 0 ? 'Create your first record' : 'No records match filters'}</p>
          {records.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Record</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer><SortContext.Provider value={sort.ctxValue}><Table><thead><tr>
          <SortableTh field="docNumber">Doc No</SortableTh><SortableTh field="docDate">Date</SortableTh>{showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}<SortableTh field="adjustmentType">Type</SortableTh><SortableTh field="reason">Reason</SortableTh>
          <SortableTh field="totalValue" style={{ textAlign: 'right' }}>Value</SortableTh><SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
          <Th style={{ textAlign: 'center' }}>Actions</Th>
        </tr></thead><tbody>
          {filteredRecords.map(rec => (
            <Tr key={rec.id}>
              <Td><strong>{rec.docNumber}</strong></Td><Td>{rec.docDate}</Td>
              {showBranchCol && <Td>{rec.branchName || '-'}</Td>}
              <Td>{rec.adjustmentType}</Td><Td>{rec.reason || '-'}</Td>
              <Td style={{ textAlign: 'right', fontWeight: 500 }}>{activeCompany?.currencyCode || 'PKR'} {formatAmount(rec.totalValue)}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[rec.status]}>{rec.status}</StatusBadge></Td>
              <Td style={{ textAlign: 'center' }}><ActionGroup>
                <IconBtn title="View" onClick={() => handleView(rec)}><FiEye /></IconBtn>
                {canChange && <IconBtn title="Edit" onClick={() => handleEdit(rec)}><FiEdit2 /></IconBtn>}
                <IconBtn title="Print" onClick={() => handlePrint(rec)}><FiPrinter /></IconBtn>
                {canDelete && <IconBtn title="Delete" $danger onClick={() => handleDelete(rec)}><FiTrash2 /></IconBtn>}
                {canEdit && rec.status !== 'Cancelled' && (
                  <IconBtn title="Cancel" onClick={() => handleCancel(rec)} style={{ color: colors.error.main }}>
                    <FiXCircle />
                  </IconBtn>
                )}
              </ActionGroup></Td>
            </Tr>
          ))}
        </tbody></Table></SortContext.Provider></TableContainer>
      )}
      <DocListPagination {...pagination} />
      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredRecords.length : pagination.totalCount}</strong> record(s)</span>
        <span>Draft: <strong>{listSummary.counts.Draft || 0}</strong> | Posted: <strong>{listSummary.counts.Posted || 0}</strong></span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingRecord) return null;
    const rec = viewingRecord;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingRecord(null); }}><FiArrowLeft /> Back to List</BackButton>
          <FormTitle>{rec.docNumber}<StatusBadge $color={STATUS_COLORS[rec.status]} style={{ marginLeft: 12 }}>{rec.status}</StatusBadge></FormTitle>
          <FormActions>
            {canChange && <SaveButton onClick={() => handleEdit(rec)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(rec)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>
        <FormCard>
          <SectionTitle>Adjustment Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Doc No</label><span>{rec.docNumber}</span></InfoItem>
            <InfoItem><label>Date</label><span>{rec.docDate}</span></InfoItem>
            <InfoItem><label>Type</label><span>{rec.adjustmentType}</span></InfoItem>
            <InfoItem><label>Reason</label><span>{rec.reason || '-'}</span></InfoItem>
            <InfoItem><label>Employee</label><span>{rec.employeeName || '-'}</span></InfoItem>
            <InfoItem><label>Job</label><span>{rec.jobName || '-'}</span></InfoItem>
            <InfoItem><label>Location</label><span>{rec.locationName || '-'}</span></InfoItem>
          </InfoGrid>
        </FormCard>
        <FormCard>
          <SectionTitle>Items</SectionTitle>
          <TableContainer><Table><thead><tr>
            <Th>#</Th><Th>Description</Th><Th style={{ textAlign: 'right' }}>Qty</Th>
            <Th style={{ textAlign: 'right' }}>Unit Cost</Th><Th style={{ textAlign: 'right' }}>Total</Th>
          </tr></thead><tbody>
            {(rec.lines || []).map((line, i) => (
              <Tr key={i}><Td>{i + 1}</Td><Td>{line.description}</Td>
                <Td style={{ textAlign: 'right' }}>{line.quantity}{line.unitOfMeasure ? ` ${line.unitOfMeasure}` : ''}</Td>
                <Td style={{ textAlign: 'right' }}>{formatAmount(line.unitCost)}</Td>
                <Td style={{ textAlign: 'right' }}>{formatAmount(line.lineTotal)}</Td>
              </Tr>
            ))}
          </tbody></Table></TableContainer>
          <TotalsSection><TotalsTable><tbody>
            <TotalRow><td>Total Value ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(rec.totalValue)}</td></TotalRow>
          </tbody></TotalsTable></TotalsSection>
        </FormCard>
        {rec.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{rec.notes}</NotesText></FormCard>}
      </>
    );
  };

  const renderFormView = () => (
    <>
      <FormHeader>
        <BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back to List</BackButton>
        <FormTitle>{editingRecord ? `Edit: ${editingRecord.docNumber}` : 'New Add Stock Adjustment'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
          {canEdit && !(editingRecord && editingRecord.status && editingRecord.status !== 'Draft' && editingRecord.status !== 'Cancelled') && <SaveButton onClick={() => handleSave('Draft')} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save Draft'}</SaveButton>}
          {canEdit && (formData.status === 'Draft' || !editingRecord) && (
            <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}><FiCheck /> Save & Post</SaveButton>
          )}
          {/* ACC-05: editing a posted document keeps it posted (no silent drop to Draft) */}
          {canEdit && (editingRecord && editingRecord.status && editingRecord.status !== 'Draft' && editingRecord.status !== 'Cancelled') && (
            <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}><FiCheck /> {saving ? 'Saving...' : 'Save Changes'}</SaveButton>
          )}
        </FormActions>
      </FormHeader>
      <FormCard>
        <SectionTitle>Adjustment Details</SectionTitle>
        <FormGrid>
          <FormGroup><Label>Doc No</Label><Input value={editingRecord?.docNumber || 'Auto-generated'} disabled /></FormGroup>
          <FormGroup><Label>Date *</Label><Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} /></FormGroup>
          <FormGroup><Label>Type *</Label>
            <Select value={formData.adjustmentType} onChange={e => handleHeaderChange('adjustmentType', e.target.value)}>
              <option value="Found">Found</option><option value="Gift">Gift</option>
              <option value="Opening">Opening</option><option value="Correction">Correction</option>
            </Select>
          </FormGroup>
          <FormGroup><Label>Status</Label><Input value={formData.status} disabled /></FormGroup>
          <FormGroup $span2><Label>Reason *</Label><Input value={formData.reason} onChange={e => handleHeaderChange('reason', e.target.value)} placeholder="Reason for adjustment" /></FormGroup>
          <FormGroup>
            <Label>Employee</Label>
            <Select value={formData.employeeId} onChange={e => {
              const emp = employees.find(em => em.id === e.target.value);
              handleHeaderChange('employeeId', e.target.value);
              handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '');
            }}>
              <option value="">-- Select Employee --</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Job</Label>
            <Select value={formData.jobId} onChange={e => {
              const job = jobs.find(j => j.id === e.target.value);
              handleHeaderChange('jobId', e.target.value);
              handleHeaderChange('jobName', job?.jobName || '');
            }}>
              <option value="">-- Select Job --</option>
              {jobs.map(job => (
                <option key={job.id} value={job.id}>{job.jobCode ? job.jobCode + ' - ' : ''}{job.jobName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>{reqLabel(activeCompany, 'Location')}</Label>
            <Select value={formData.locationId} onChange={e => {
              const loc = locations.find(l => l.id === e.target.value);
              handleHeaderChange('locationId', e.target.value);
              handleHeaderChange('locationName', loc?.locationName || '');
            }}>
              <option value="">-- Select Location --</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.locationName}</option>
              ))}
            </Select>
          </FormGroup>
        </FormGrid>
      </FormCard>
      <FormCard>
        <LineItemsHeader>
          <SectionTitle style={{ margin: 0 }}>Items</SectionTitle>
          <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>
        </LineItemsHeader>
        <LineTableContainer><LineTable><thead><tr>
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh>
          <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Item</LineTh>
          <LineTh style={{ width: 260, minWidth: 260, maxWidth: 260 }}>Description</LineTh>
          <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Qty</LineTh>
          <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Unit Cost</LineTh>
          <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Total</LineTh>
          {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Batch #</LineTh>}
          {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 150, minWidth: 150, maxWidth: 150 }}>Expiry Date</LineTh>}
          {lineItems.some(l => l.trackingType === 'Serial') && <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Serial Numbers</LineTh>}
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
        </tr></thead><tbody>
          {lineItems.map((line, index) => {
            const calc = calculateLine(line);
            return (
              <tr key={index}>
                <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                <LineTd><ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={items} selectedItemId={line.itemId} onChange={(itemId) => handleLineChange(index, 'itemId', itemId)} stockBalances={stockBalances} stockOnDemand onRequestStock={requestItemStock} /></LineTd>
                <LineTd><LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)} placeholder="Description" /></LineTd>
                <LineTd><div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><LineInput type="number" min="0" value={line.quantity} onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))} style={{ textAlign: 'right' }} /><LineUnitPicker item={items.find(i => i.id === line.itemId)} line={line} onChange={(uc) => handleLineChange(index, 'unitCode', uc)} /></div></LineTd>
                <LineTd><LineInput type="number" min="0" step="0.01" value={line.unitCost} onChange={e => handleLineChange(index, 'unitCost', Number(e.target.value))} onKeyDown={(e) => handleLastFieldEnter(e, index)} style={{ textAlign: 'right' }} /></LineTd>
                <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
                {lineItems.some(l => l.trackingType === 'Batch') && (
                  <LineTd>{line.trackingType === 'Batch' ? <LineInput value={line.batchNumber} onChange={e => handleLineChange(index, 'batchNumber', e.target.value)} placeholder="Batch #" /> : <span style={{ color: colors.text.secondary }}>-</span>}</LineTd>
                )}
                {lineItems.some(l => l.trackingType === 'Batch') && (
                  <LineTd>{line.trackingType === 'Batch' ? <LineInput type="date" value={line.expiryDate || ''} onChange={e => handleLineChange(index, 'expiryDate', e.target.value)} /> : <span style={{ color: colors.text.secondary }}>-</span>}</LineTd>
                )}
                {lineItems.some(l => l.trackingType === 'Serial') && (
                  <LineTd>{line.trackingType === 'Serial' ? <LineInput value={line.serialText !== undefined ? line.serialText : (line.serialNumbers || []).join(', ')} onChange={e => handleLineChange(index, 'serialText', e.target.value)} onBlur={e => { const parsed = e.target.value.split(',').map(s => s.trim()).filter(Boolean); handleLineChange(index, 'serialNumbers', parsed); handleLineChange(index, 'serialText', undefined); }} placeholder={`${line.quantity || 0} serial(s), comma-separated`} /> : <span style={{ color: colors.text.secondary }}>-</span>}</LineTd>
                )}
                <LineTd style={{ textAlign: 'center' }}>{lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)}><FiX /></RemoveLineBtn>}</LineTd>
              </tr>
            );
          })}
        </tbody></LineTable></LineTableContainer>
        <TotalsSection><TotalsTable><tbody>
          <TotalRow><td>Total Value ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(totals.totalValue)}</td></TotalRow>
        </tbody></TotalsTable></TotalsSection>
      </FormCard>
      <FormCard>
        <SectionTitle>Notes</SectionTitle>
        <FormGrid><FormGroup $span2><TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Additional notes..." rows={3} /></FormGroup></FormGrid>
      </FormCard>
    </>
  );

  useDocumentShortcuts({
    onNew: () => { if (!canEdit) { alert('You do not have permission to create.'); return; } handleCreate(); },
    onSave: () => { if (!canEdit) { alert('You do not have permission to save changes.'); return; } handleSave('Posted'); },
    viewMode,
    saving
  });

  return (
    <PageLayout pageTitle="Add Stock" breadcrumb="Store / Add Stock">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

export default AddStock;
